import React, {
    ReactElement, useCallback, useEffect, useState,
} from "react";
import { Modal } from "react-bootstrap";
import { useDropzone } from "react-dropzone";
import { AiFillCaretDown, AiFillCaretLeft } from "react-icons/ai";
import { RiCloseLine } from "react-icons/ri";
import TimeAgo from "react-timeago";
import useKeyboardShortcuts, { useKeyboardState } from "../../contexts/KeyboardShortcutContext";
import usePortal from "../../contexts/PortalContext";
import useEvents from "../../contexts/PortalSubscriptionContext";
import useRouting, { useParams } from "../../contexts/Routing";
import { AssignmentType } from "../../contexts/selection-state/selection-state";
import useSelectionState from "../../contexts/selection-state/SelectionStateContext";
import { NoDataOrErrorView } from "../../pages/error/Error";
import {
    Chat as ApiChat, ChatStatus, Form as ApiForm, GetChatDocument, ListAgentChatDocument,
    ListAgentChatQuery, PortalChatResyncEvent, PortalEventType, PortalMessageEvent,
    PortalNoteAddedEvent, Ticket as RawTicket, TicketCustomContextValue, TicketDueDate, TicketInput,
    TicketMessage, TicketMessageType, TicketPriority, TicketStatus, TicketTask,
    useCreateUploadTokenMutation, useGetChatQuery, UserRole, useSaveTicketMutation,
} from "../../__generated__/api";
import ActionExecute from "../action-execute/ActionExecute";
import ArtificialIntelligence from "../agent/artificial-intelligence/ArtificialIntelligence";
import { PRIORITY_ICON } from "../agent/card/Card";
import TextEntry, { UploadedFile } from "../agent/text-entry/text-entry";
import Avatar from "../avatar/Avatar";
import DisplayValue from "../display-value/DisplayValue";
import EditableControl, { ControlType } from "../editable-text/EditableText";
import Form, { PRIORITY_ID, REQUESTOR_ID, TAGS_ID } from "../form/Form";
import Loading from "../loading/Loading";
import Resizable from "../resizable/Resizable";
import TicketDueDates from "../sla-dates/TicketDueDates";
import Tags from "../tags/Tags";
import TicketTasks from "../tasks/Tasks";
import Actions from "./Actions";
import Cards from "./cards";
import { ChatSideBar } from "./chat-side-bar";
import Styles from "./Chat.module.scss";
import { ChatStateContext, ChatStateProvider } from "./chat-state";

export type Ticket = Exclude<ListAgentChatQuery["chats"], undefined | null>["chats"][0]

const STATUS_TEXT = {

    [ChatStatus.Assigned]: "Assigned",
    [ChatStatus.ChatRequested]: "Waiting",
    [ChatStatus.Connected]: "Connected",
    [ChatStatus.Finished]: "Finished",
    [ChatStatus.Ignored]: "Ignored",
    [ChatStatus.OnHold]: "On Hold",
    [ChatStatus.Snoozed]: "Snoozed",
    [ChatStatus.DeflectionWaiting]: "Deflection Waiting",
};

function CustomContextValues(props: {items: TicketCustomContextValue[] | null | undefined}): ReactElement {
    return (
        <div>
            {props.items?.map(contextLine => (
                <div
                    key={contextLine.id}
                    className="mb-1 d-flex justify-content-center  align-items-center"
                    title={contextLine.description}
                >
                    <small className=" text-gray-500 col-3">
                        {contextLine.label}
                    </small>
                    <small className="col text-wrap text-truncate ms-2">
                        <DisplayValue
                            text={contextLine.displayText ?? contextLine.value.string as string}
                            type={contextLine.displayType}
                            value={contextLine.value.string as string}
                        />
                    </small>
                </div>
            ))}
        </div>
    );
}

const KEYBOARD_CONTEXT = "chat";
const FEATURE_DARKER = "frontend.design.darker";

function ChatBody(props: { ticketNumber: string}): ReactElement | null {
    const {
        loading, data, error, refetch,
    } = useGetChatQuery({
        variables: {
            id: props.ticketNumber,
        },
        skip: !props.ticketNumber,
        fetchPolicy: "no-cache",
    });

    const [messages, setMessages] = useState<TicketMessage[]>();
    const [saveTicket] = useSaveTicketMutation();
    const selectionState = useSelectionState();
    const { isAIEnabled, featureFlags } = usePortal();
    useKeyboardShortcuts(api => {
        api.on({
            context: KEYBOARD_CONTEXT,
            shortcut: "esc",
            handler: evt => {
                evt?.stopPropagation();
                selectionState.closeCurrent();
            },
        });
    });

    const [uploadAttachment] = useCreateUploadTokenMutation();
    const [currentFiles, setCurrentFiles] = useState<UploadedFile[]>([]);
    const [chatStatus, assignment] = selectionState.registerChatStatusAssignment(
        data?.chat?.id,
        data?.chat?.status ?? ChatStatus.ChatRequested,
        data?.chat?.isAssignedToCurrentUser === true ? AssignmentType.CURRENT_USER : AssignmentType.NONE,
    );

    const [isTaskExpanded, setTaskExpanded] = useState(true);
    const [isDueDateHistoryExpanded, setDueDateHistoryExpanded] = useState(false);

    const [action, setAction] = useState<ApiForm>();

    const isAssignedToCurrentUser = assignment === AssignmentType.CURRENT_USER;

    const canUserEdit = data?.chat?.canCurrentUserEdit || isAssignedToCurrentUser;

    useEvents(api => {
        api.on(PortalEventType.Resync, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === selectionState.currentId) {
                refetch();
            }
        });
        api.on(PortalEventType.ParticipantsAdded, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === selectionState.currentId) {
                refetch();
            }
        });
        api.on(PortalEventType.ParticipantsRemoved, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === selectionState.currentId) {
                refetch();
            }
        });
        api.on(PortalEventType.ReactionAdded, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === selectionState.currentId) {
                refetch();
            }
        });
        api.on(PortalEventType.ReactionRemoved, 10, (event: PortalChatResyncEvent) => {
            if (event.chatId === selectionState.currentId) {
                refetch();
            }
        });
        api.on(PortalEventType.MessageNew, 10, (event: PortalMessageEvent) => {
            if (event.chatId === selectionState.currentId) {
                setMessages(current => {
                    const values = current ?? [];
                    return [
                        ...values,
                        {
                            body: event.message,
                            createdDate: event.date,
                            creatorId: event.sender?.id,
                            creator: {
                                id: event.sender?.id,
                                avatarUrl: event.sender?.iconUrl,
                                name: event.sender?.name,
                                userGroups: [],
                                role: UserRole.Guest,
                            },
                            type: event.type,
                            id: event.messageId ?? Date.now(),
                            attachments: event.attachments?.map(attach => ({
                                urlSha256: attach.urlSha256,
                                url: attach.imageUrl,
                                fileName: attach.title,
                                mimeType: attach.mimeType,
                                title: attach.title,
                            })),
                            articles: event.articles,
                        },
                    ];
                });
                selectionState.markRead(event.chatId as string);
            }
        });

        const messageEdited = (event: PortalMessageEvent) => {
            if (event.chatId === selectionState.currentId) {
                setMessages(current => {
                    const copy = [...(current ?? [])];
                    const messageIndex = copy.findIndex(row => row.id === event.messageId);
                    copy[messageIndex] = {
                        body: event.message,
                        createdDate: event.date,
                        creatorId: event.sender?.id,
                        creator: {
                            id: event.sender?.id,
                            avatarUrl: event.sender?.iconUrl,
                            name: event.sender?.name,
                            userGroups: [],
                            role: UserRole.Guest,
                        },
                        originalBody: event.originalBody,
                        isDeleted: event.isDeleted,
                        type: event.type,
                        id: event.messageId ?? Date.now(),
                        attachments: event.attachments?.map(attach => ({
                            urlSha256: attach.urlSha256,
                            url: attach.imageUrl,
                            fileName: attach.title,
                            mimeType: attach.mimeType,
                            title: attach.title,
                        })),
                        articles: event.articles,
                    };
                    return copy;
                });
                selectionState.markRead(event.chatId as string);
            }
        };

        api.on(PortalEventType.MessageEdited, 10, messageEdited);
        api.on(PortalEventType.MessageDeleted, 10, messageEdited);

        api.on(PortalEventType.NoteAdded, 10, (event: PortalNoteAddedEvent) => {
            if (event.chatId === selectionState.currentId) {
                setMessages(current => {
                    const values = current ?? [];
                    return [
                        ...values,
                        {
                            body: event.message,
                            createdDate: event.date,
                            creatorId: event.sender?.id,
                            creator: {
                                id: event.sender?.id,
                                avatarUrl: event.sender?.iconUrl,
                                name: event.sender?.name,
                                userGroups: [],
                                role: UserRole.Guest,
                            },
                            type: TicketMessageType.Comment,
                            id: event.messageId ?? Date.now(),
                            attachments: event.attachments?.map(attach => ({
                                urlSha256: attach.urlSha256,
                                url: attach.imageUrl,
                                fileName: attach.title,
                                mimeType: attach.mimeType,
                                title: attach.title,
                            })),
                        },
                    ];
                });
                selectionState.markRead(event.chatId as string);
            }
        });
    });

    useEffect(() => {
        const loadedMessages = data?.chat?.ticket?.messages;
        if (loadedMessages) {
            setMessages((loadedMessages.sortBy(msg => new Date(msg.createdDate).getTime()) ?? []) as TicketMessage[]);
        }
        const id = data?.chat?.id;
        selectionState.setOpened(id);
        selectionState.setChatStatus(
            id,
            data?.chat?.status ?? ChatStatus.ChatRequested,
            data?.chat?.isAssignedToCurrentUser === true ? AssignmentType.CURRENT_USER : AssignmentType.NONE,
        );

        if (data?.chat && !selectionState.hasChat(data?.chat?.id)) {
            selectionState.addItem(data.chat as Ticket);
        }
    }, [data]);

    const uploadFile = async (file: File) => {
        const meta = await uploadAttachment({
            variables: {
                attachment: {
                    filename: file.name,
                    size: file.size,
                    mimeType: file.type,
                },
            },
        });

        if (!meta.data?.createUploadToken || meta.data.createUploadToken?.error) {
            return {
                name: file.name,
                mimeType: file.type,
                error: meta.data?.createUploadToken?.error ?? "Error uploading file",
            };
        }
        const { url, token } = meta.data.createUploadToken;

        const response = await fetch(url, {
            method: "POST",
            body: file,
            credentials: "include",
        });

        if (response.status !== 201) {
            return {
                name: file.name,
                mimeType: file.type,
                error: "Error uploading file. Server did not return 201",
            };
        }

        const json = await response.json() as {url: string};

        return {
            name: file.name,
            mimeType: file.type,
            url: json.url,
            token,
        };
    };

    const onFilesAdded = useCallback(async (acceptedFiles: File[]) => {
        setCurrentFiles(current => (current ?? []).concat(acceptedFiles.map(file => ({
            name: file.name,
            mimeType: file.type,
            isUploading: true,
        }))));

        const files = await acceptedFiles.mapConcurrent(async (file: File): Promise<UploadedFile> => uploadFile(file));
        setCurrentFiles(current => (current ?? []).filter(row => !row.isUploading).concat(files));
    }, []);
    const {
        getRootProps, getInputProps, open, isDragActive,
    } = useDropzone({ onDrop: onFilesAdded, noClick: true });

    const value = data?.chat;

    // eslint-disable-next-line arrow-body-style
    const createClose = () => {
        selectionState.closeCurrent();
    };

    const isDarker = featureFlags.has(FEATURE_DARKER);
    const headerColor = isDarker ? "bg-blue-dark" : "bg-gradient";

    if (loading) {
        return (
            <div className="d-flex flex-column h-100">
                <div className={`card-header ${headerColor} text-white d-flex justify-content-between align-items-center`}>
                    <div>
                        {loading ? "Loading Chat..." : "Error Occurred"}
                    </div>
                    <button type="button" className="btn btn-none m-0 p-1 px-2" onClick={createClose}>
                        <RiCloseLine className="text-light" />
                    </button>
                </div>
                <div className="overflow-auto w-100 h-100">
                    <div className={`centered-container-lg m-auto w-100 h-100 flex-stretch-0 `}>
                        {loading ? <Loading /> : (
                            <NoDataOrErrorView error={error} />
                        )}
                    </div>
                </div>

            </div>
        );
    }

    if (!value) {
        return null;
    }

    const createdDate = new Date(value.ticket?.createdDate);
    const closedDate = (value.ticket?.closedDate
        && (value.ticket?.status === TicketStatus.Closed || value.ticket?.status === TicketStatus.Ignored))
        ? new Date(value.ticket.closedDate)
        : undefined;
    const canEdit = (isAssignedToCurrentUser === true || value.canCurrentUserEdit === true)
            && (value.ticket?.status === TicketStatus.Open
                || value.ticket?.status === TicketStatus.Reopened
                || value.ticket?.status === TicketStatus.Hold
            );

    const updateTicket = async (ticket: Partial<TicketInput>) => {
        await saveTicket({
            refetchQueries: [
                { query: GetChatDocument, variables: { id: props.ticketNumber } },
                { query: ListAgentChatDocument },
            ],
            variables: {
                id: value.ticket?.id,
                ticket,
            },
        });
        if (ticket.requestorId) {
            refetch();
        }
    };

    const handleChangeRequestType = (oldForm: ApiForm | undefined, newForm: ApiForm) => {
        if (oldForm?.id !== newForm.id && newForm.hasAutomation) {
            setAction(newForm);
        }
    };

    return (
        <>
            <div className={`card-header ${headerColor} text-white d-flex justify-content-between align-items-center`}>
                <div className="flex-grow-1">
                    {value?.ticket?.ticketNumber && (
                        <span className="me-4 text-light flex-grow-0">
                            #
                            {value?.ticket?.ticketNumber}
                        </span>
                    )}
                    Chat with
                    {" "}
                    {(value.ticket?.requestor ?? value.creator).name}
                    {": "}
                    {value.ticket?.subject}

                </div>

                <button type="button" className="btn btn-none m-0 p-1 px-2" onClick={createClose}>
                    <RiCloseLine className="text-light" />
                </button>

            </div>

            <div className="card-body  d-flex w-100 p-0 align-items-stretch align-self-stretch">
                <ChatSideBar value={value as ApiChat} />

                {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                <div
                    className={`flex-grow-1 col h-100 d-flex flex-column shadow ${Styles.messages} cursor-default`}
                    // eslint-disable-next-line react/jsx-props-no-spreading,
                    {...getRootProps()}

                >
                    {isDragActive && (
                        <div
                            className="fs-4 text-light d-flex justify-content-center align-items-center flex-grow-1"
                        >
                            Drag and Drop files here
                        </div>
                    )}

                    {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                    <input {...getInputProps()} />

                    <ChatStateProvider>
                        <>
                            {messages && !isDragActive && (
                                <Cards
                                    chatId={data.chat?.id as string}
                                    messages={messages}
                                    isEditable={data.chat?.source?.isEditable ?? false}
                                />
                            )}
                            {selectionState.hasChat(value.id) && (
                                <TextEntry
                                    className={isDragActive ? "d-none" : ""}
                                    attachments={currentFiles}
                                    status={chatStatus}
                                    id={value.id}
                                    sourceId={value.source?.id as string}
                                    onOpen={open}
                                    canUserEdit={canUserEdit}
                                    canAddArticles
                                    draft={messages?.find(msg => msg.type === TicketMessageType.Draft)}
                                    onUploadAttachments={onFilesAdded}
                                    onSent={() => {
                                        if (chatStatus !== ChatStatus.Assigned && !value.isCurrentUserParticipating) {
                                            selectionState.setChatStatus(
                                                selectionState.currentIdOrError,
                                                ChatStatus.Assigned,
                                                AssignmentType.CURRENT_USER,
                                            );
                                        }
                                        setCurrentFiles([]);
                                    }}
                                    onRemoveAttachment={attachments => setCurrentFiles(attachments)}
                                    canParticipate={
                                        (chatStatus === ChatStatus.Assigned
                                    || chatStatus === ChatStatus.OnHold
                                    || chatStatus === ChatStatus.Snoozed)
                                                    && !isAssignedToCurrentUser
                                                    && !value.isCurrentUserParticipating
                                    }
                                />
                            )}
                        </>
                    </ChatStateProvider>

                </div>

                <Resizable
                    direction="w"
                    defaultSize={350}

                >

                    <div className="h-100 d-flex flex-column">

                        <div className={`${Styles["info-container"]} flex-grow-1`}>
                            <div className="list-group list-group-flush p-4">

                                <h6 className="col-3">
                                    Context
                                </h6>

                                {value.source && (
                                    <div className="mb-1 d-flex justify-content-center align-items-center">
                                        <small className=" text-gray-500 col-3">
                                            Source
                                        </small>
                                        <div className="col ms-2">
                                            <a
                                                href={value.source.url as string}
                                                className="text-link text-decoration-none"
                                                target="_blank"
                                                rel="noreferrer"
                                            >
                                                {value.source.text}
                                            </a>
                                        </div>

                                    </div>
                                )}

                                <div className="mb-1 d-flex justify-content-center align-items-center">
                                    <small className=" text-gray-500 col-3">
                                        Status
                                    </small>
                                    <div className="col ms-2">
                                        {STATUS_TEXT[chatStatus]}
                                    </div>

                                </div>

                                {!isAssignedToCurrentUser && value.assignee && (
                                    <div className="mb-1 d-flex justify-content-center">
                                        <small className=" text-gray-500 col-3">
                                            Assigned
                                        </small>
                                        <div className="col ms-2">
                                            <div className="d-flex align-items-center">
                                                <Avatar src={value.assignee.avatarUrl as string} variant="xxs" />
                                                <span>
                                                    {value.assignee.name}
                                                </span>
                                            </div>
                                            {value.isCurrentUserParticipating && (
                                                <div className="badge bg-light">
                                                    You are a participant
                                                </div>
                                            )}
                                        </div>

                                    </div>
                                )}

                                <div className="mb-1 d-flex justify-content-center  align-items-center">
                                    <small className=" text-gray-500 col-3">
                                        Priority
                                    </small>
                                    <div className="col ms-2">

                                        <EditableControl
                                            isReadOnly={!canEdit}
                                            value={value.ticket?.priority ?? TicketPriority.Medium}
                                            placeholder="Enter Priority"
                                            type={ControlType.SELECT}
                                            options={(Object.entries(PRIORITY_ICON).map(([key, label]) => ({
                                                label,
                                                value: key,
                                            })))}
                                            onChange={changed => updateTicket({ priority: changed as TicketPriority })}
                                        />

                                    </div>
                                </div>
                                <div className="mb-1 d-flex justify-content-center  align-items-center">
                                    <small className=" text-gray-500 col-3">
                                        Created
                                    </small>
                                    <div className="col ms-2">
                                        <TimeAgo
                                            date={new Date(createdDate)}
                                            minPeriod={60}
                                            title={`${createdDate.toLocaleDateString()} ${createdDate.toLocaleTimeString()}`}
                                        />
                                    </div>
                                </div>

                                <div className="mb-1 d-flex justify-content-center  align-items-center">
                                    <small className=" text-gray-500 col-3">
                                        Requester
                                    </small>
                                    <div className="col ms-2">

                                        <EditableControl
                                            isReadOnly={!canEdit}
                                            value={value.ticket?.requestorId ?? value.creatorId}
                                            placeholder="Enter requester"
                                            type={ControlType.USER}
                                            canCreate
                                            onChange={changed => updateTicket({ requestorId: changed as string })}
                                        >
                                            <span>
                                                <Avatar
                                                    src={value.ticket?.requestor.avatarUrl ?? value.creator.avatarUrl as string}
                                                    variant="xxs"
                                                />
                                                <span>
                                                    {value.ticket?.requestor.name ?? value.creator.name}
                                                </span>
                                            </span>

                                        </EditableControl>

                                    </div>
                                </div>

                                {closedDate && (
                                    <div className="mb-1 d-flex justify-content-center  align-items-center">
                                        <small className=" text-gray-500 col-3">
                                            Completed
                                        </small>
                                        <div className="col ms-2">
                                            <TimeAgo
                                                date={new Date(closedDate)}
                                                minPeriod={60}
                                                title={`${closedDate.toLocaleDateString()} `
                                                            + `${closedDate.toLocaleTimeString()}`}
                                            />
                                        </div>
                                    </div>
                                )}

                                {(canEdit || value.ticket?.tags?.isNotEmpty) && (
                                    <div className="mb-1 d-flex justify-content-center  align-items-center">
                                        <small className=" text-gray-500 col-3">
                                            Tags
                                        </small>
                                        <div className="col ms-2">
                                            <Tags
                                                isReadOnly={!canEdit}
                                                value={value.ticket?.tags}
                                                onChange={tags => updateTicket({ tags: tags as string[] })}
                                            />
                                        </div>
                                    </div>
                                )}

                                <CustomContextValues items={value.ticket?.customContextValues as TicketCustomContextValue[]} />

                                {value.ticket?.tasks?.isNotEmpty && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mt-4 mb-1">
                                            <h6 className="p-0 m-0">
                                                Tasks
                                            </h6>
                                            <button
                                                type="button"
                                                className="btn btn-none p-0 m-0"
                                                onClick={() => setTaskExpanded(current => !current)}
                                            >
                                                {isTaskExpanded
                                                    ? <AiFillCaretDown className="text-gray-400" />
                                                    : <AiFillCaretLeft className="text-gray-400" />}
                                            </button>
                                        </div>
                                        {isTaskExpanded && (
                                            <TicketTasks tasks={value.ticket.tasks as TicketTask[]} />
                                        )}
                                    </div>
                                )}

                                {isAIEnabled && (
                                    <ArtificialIntelligence chatId={value.id} />
                                )}

                                {value.ticket?.dueDates?.isNotEmpty && (
                                    <div className="mb-3">
                                        <div className="d-flex justify-content-between align-items-center mt-4 mb-1">
                                            <h6 className="p-0 m-0">
                                                SLAs
                                            </h6>
                                            <button
                                                type="button"
                                                className="btn btn-none p-0 m-0"
                                                onClick={() => setDueDateHistoryExpanded(current => !current)}
                                            >
                                                {isDueDateHistoryExpanded
                                                    ? <AiFillCaretDown className="text-gray-400" />
                                                    : <AiFillCaretLeft className="text-gray-400" />}
                                            </button>
                                        </div>
                                        <TicketDueDates
                                            dueDates={value.ticket.dueDates as TicketDueDate[]}
                                            isDueDateHistoryExpanded={isDueDateHistoryExpanded}
                                        />

                                    </div>
                                )}

                                <h6 className="mt-4">
                                    Request
                                </h6>
                                <Form
                                    ticket={value.ticket as RawTicket}
                                    isEditable={canEdit}
                                    canSelectRequestType
                                    shouldValidateAutomations
                                    onChange={updateTicket}
                                    onChangeRequestType={handleChangeRequestType}
                                    shouldValidateAutomation
                                    skipFields={[
                                        PRIORITY_ID,
                                        REQUESTOR_ID,
                                        TAGS_ID,
                                    ]}
                                />
                                {action && (
                                    <ActionExecute
                                        actionId={action.id}
                                        chat={value as ApiChat}
                                        chatId={value.id}
                                        onHide={() => setAction(undefined)}
                                        onEmptyMessage=""
                                        shouldSaveForm
                                        onExecuted={() => refetch()}
                                    >
                                        <div className="mb-3">
                                            <div className="alert alert-info">
                                                <h5 className="d-block">Automations Detected</h5>
                                                <span>
                                                    The request type &quot;
                                                    {action.name}
                                                    &quot;
                                                    has some automations which
                                                    would run when the ticket is created.
                                                    Do you want to execute them now?
                                                </span>
                                            </div>
                                            {action.fields.isNotEmpty && (
                                                <>
                                                    <strong className="mt-4 d-block">Ticket Overrides</strong>
                                                    <small>
                                                        Here are the fields used by &quot;
                                                        {action.name}
                                                        &quot;.
                                                        Override them below and click execute to fire the automations.
                                                    </small>
                                                </>
                                            )}

                                        </div>
                                    </ActionExecute>
                                )}
                            </div>

                        </div>

                        {selectionState.currentId && selectionState.hasChat(selectionState.currentId) && (
                            <Actions
                                canEdit={canEdit}
                                value={value}
                                canUserEdit={canUserEdit}
                                chatStatus={chatStatus}
                            />
                        )}
                    </div>

                </Resizable>

            </div>
        </>
    );
}

export default function Chat(): ReactElement {
    const { Paths } = useRouting();
    const { chatId: ticketNumber } = useParams(Paths.AGENT_CHAT);

    const keyboardState = useKeyboardState();
    useEffect(() => {
        if (ticketNumber === "new") {
            return;
        }
        if (ticketNumber) {
            keyboardState.pushContext(KEYBOARD_CONTEXT);
        } else {
            keyboardState.popContext(KEYBOARD_CONTEXT);
        }
    }, [ticketNumber]);

    return (
        <Modal
            show={ticketNumber != null && ticketNumber !== "new"}
            size="lg"
            // onHide={() => selectionState.closeCurrent()}
            fullscreen="lg-down"
            dialogClassName={`${Styles.container} card bg-white shadow border-0 rounded`}
            contentClassName="h-100 border-0 rounded"
            enforceFocus={false}
        >
            <Modal.Body className="h-100 w-100 m-0 p-0 d-flex flex-column text-gray-800">
                <ChatBody
                    ticketNumber={ticketNumber}
                />
            </Modal.Body>

        </Modal>
    );
}
